import * as Tone from 'tone';
import { loadSoundfontInstrument } from './soundfontInstruments';

// Global setting to control soundfont usage
let useSoundfonts = true;

export const setUseSoundfonts = (enabled) => {
  useSoundfonts = enabled;
};

export const getUseSoundfonts = () => useSoundfonts;

// Base instrument definitions without labels (labels will be generated with localization)
const baseInstrumentDefinitions = [
  // Enhanced Instruments (now with soundfont support)
  { value: 'piano', labelKey: 'piano', icon: '🎹', type: 'enhanced' },
  { value: 'electric_piano', labelKey: 'electricPiano', icon: '🎹', type: 'enhanced' },
  { value: 'strings', labelKey: 'strings', icon: '🎻', type: 'enhanced' },
  { value: 'guitar', labelKey: 'guitar', icon: '🎸', type: 'enhanced' },
  { value: 'bass', labelKey: 'bass', icon: '🎸', type: 'enhanced' },
  { value: 'brass', labelKey: 'brass', icon: '🎺', type: 'enhanced' },
  { value: 'flute', labelKey: 'flute', icon: '🎵', type: 'enhanced' },
  { value: 'organ', labelKey: 'organ', icon: '🎹', type: 'enhanced' },
  { value: 'bells', labelKey: 'bells', icon: '🔔', type: 'enhanced' },
  { value: 'choir', labelKey: 'choir', icon: '👥', type: 'enhanced' },
  { value: 'harp', labelKey: 'harp', icon: '🎯', type: 'enhanced' },
  { value: 'saxophone', labelKey: 'saxophone', icon: '🎷', type: 'enhanced' },
  { value: 'clarinet', labelKey: 'clarinet', icon: '🎼', type: 'enhanced' },
  
  // Original synths
  { value: 'Synth_fatsawtooth', labelKey: 'synthFatSaw', icon: '', type: 'synth' },
  { value: 'Synth_fatsine', labelKey: 'synthFatSine', icon: '', type: 'synth' },
  { value: 'Synth_fatsquare', labelKey: 'synthFatSquare', icon: '', type: 'synth' },
  { value: 'Synth_fattriangle', labelKey: 'synthFatTriangle', icon: '', type: 'synth' },
  { value: 'AMSynth', labelKey: 'amSynth', icon: '', type: 'synth' },
  { value: 'FMSynth', labelKey: 'fmSynth', icon: '', type: 'synth' },
  { value: 'PluckSynth', labelKey: 'pluckSynth', icon: '', type: 'synth' },
  { value: 'MembraneSynth', labelKey: 'membraneSynth', icon: '', type: 'synth' }
];

// Function to generate localized instrument options
export const getLocalizedInstrumentOptions = (t) => {
  return baseInstrumentDefinitions.map(instrument => ({
    value: instrument.value,
    label: instrument.type === 'enhanced' 
      ? `${instrument.icon} ${t(`instruments.${instrument.labelKey}`)} ${t('instruments.soundfontSuffix')}`
      : t(`instruments.${instrument.labelKey}`),
    type: instrument.type
  }));
};

// Fallback for components that still expect the old format
export const instrumentOptions = baseInstrumentDefinitions.map(instrument => ({
  value: instrument.value,
  label: instrument.type === 'enhanced' 
    ? `${instrument.icon} ${instrument.labelKey} (SF)`
    : instrument.labelKey,
  type: instrument.type
}));

export const synthTypes = {
  Synth: Tone.Synth,
  AMSynth: Tone.AMSynth,
  FMSynth: Tone.FMSynth,
  PluckSynth: Tone.PluckSynth,
  MembraneSynth: Tone.MembraneSynth
};

export const defaultEnvelope = { attack: 0.01, decay: 0.2, sustain: 0.1, release: 0.3 };
export const pluckEnvelope = { attack: 0.001, decay: 0.5, sustain: 0.01, release: 0.1 };
export const membraneEnvelope = { attack: 0.001, decay: 0.4, sustain: 0.01, release: 0.8, octaves: 6 };

export const createEnhancedInstrument = async (instrumentType, volume) => {
  // Use soundfont if enabled and available
  if (useSoundfonts) {
    try {
      const soundfontInstrument = await loadSoundfontInstrument(instrumentType, volume);
      return soundfontInstrument;
    } catch (error) {
      // Fall through to original synth creation
    }
  }

  // Original enhanced synth creation (fallback)
  let synth, effects = [];
  
  switch(instrumentType) {
    case 'piano':
      synth = new Tone.Synth({
        oscillator: { type: 'triangle8' },
        envelope: { 
          attack: 0.02, 
          decay: 0.3, 
          sustain: 0.1, 
          release: 1.2 
        },
        volume: volume
      });
      const pianoReverb = new Tone.Reverb({ 
        decay: 1.5, 
        wet: 0.3 
      });
      const pianoCompressor = new Tone.Compressor(-30, 3);
      synth.connect(pianoCompressor);
      pianoCompressor.connect(pianoReverb);
      pianoReverb.toDestination();
      effects = [pianoReverb, pianoCompressor];
      break;
      
    case 'electric_piano':
      synth = new Tone.FMSynth({
        harmonicity: 2,
        modulationIndex: 3,
        envelope: { 
          attack: 0.01, 
          decay: 0.2, 
          sustain: 0.2, 
          release: 0.8 
        },
        volume: volume
      });
      const epChorus = new Tone.Chorus(4, 2.5, 0.5);
      synth.connect(epChorus);
      epChorus.toDestination();
      effects = [epChorus];
      break;
      
    case 'strings':
      synth = new Tone.Synth({
        oscillator: { type: 'sawtooth' },
        envelope: { 
          attack: 0.3, 
          decay: 0.4, 
          sustain: 0.6, 
          release: 2.0 
        },
        volume: volume - 3
      });
      const stringFilter = new Tone.Filter(1200, 'lowpass');
      const stringReverb = new Tone.Reverb({ 
        decay: 2.5, 
        wet: 0.5 
      });
      synth.connect(stringFilter);
      stringFilter.connect(stringReverb);
      stringReverb.toDestination();
      effects = [stringFilter, stringReverb];
      break;
      
    case 'guitar':
      synth = new Tone.PluckSynth({
        attackNoise: 1,
        dampening: 4000,
        resonance: 0.9,
        volume: volume
      });
      const guitarDelay = new Tone.FeedbackDelay(0.125, 0.2);
      synth.connect(guitarDelay);
      guitarDelay.toDestination();
      effects = [guitarDelay];
      break;
      
    case 'bass':
      synth = new Tone.Synth({
        oscillator: { type: 'square' },
        envelope: { 
          attack: 0.01, 
          decay: 0.3, 
          sustain: 0.4, 
          release: 0.8 
        },
        volume: volume + 3
      });
      const bassFilter = new Tone.Filter(400, 'lowpass');
      synth.connect(bassFilter);
      bassFilter.toDestination();
      effects = [bassFilter];
      break;
      
    case 'brass':
      synth = new Tone.Synth({
        oscillator: { type: 'square4' },
        envelope: { 
          attack: 0.05, 
          decay: 0.2, 
          sustain: 0.8, 
          release: 1.0 
        },
        volume: volume
      });
      const brassDistortion = new Tone.Distortion({ 
        distortion: 0.2,
        wet: 0.3 
      });
      synth.connect(brassDistortion);
      brassDistortion.toDestination();
      effects = [brassDistortion];
      break;
      
    case 'flute':
      synth = new Tone.Synth({
        oscillator: { type: 'sine' },
        envelope: { 
          attack: 0.1, 
          decay: 0.3, 
          sustain: 0.4, 
          release: 1.5 
        },
        volume: volume - 2
      });
      const fluteFilter = new Tone.Filter(2000, 'highpass');
      const fluteReverb = new Tone.Reverb({ 
        decay: 1.0, 
        wet: 0.4 
      });
      synth.connect(fluteFilter);
      fluteFilter.connect(fluteReverb);
      fluteReverb.toDestination();
      effects = [fluteFilter, fluteReverb];
      break;
      
    case 'organ':
      synth = new Tone.Synth({
        oscillator: { type: 'square' },
        envelope: { 
          attack: 0.01, 
          decay: 0.1, 
          sustain: 0.9, 
          release: 0.3 
        },
        volume: volume
      });
      const organVibrato = new Tone.Vibrato(6, 0.1);
      synth.connect(organVibrato);
      organVibrato.toDestination();
      effects = [organVibrato];
      break;
      
    case 'bells':
      synth = new Tone.FMSynth({
        harmonicity: 8,
        modulationIndex: 12,
        envelope: { 
          attack: 0.001, 
          decay: 2.0, 
          sustain: 0.0, 
          release: 2.0 
        },
        volume: volume
      });
      const bellsReverb = new Tone.Reverb({ 
        decay: 4.0, 
        wet: 0.7 
      });
      synth.connect(bellsReverb);
      bellsReverb.toDestination();
      effects = [bellsReverb];
      break;
      
    case 'choir':
      synth = new Tone.Synth({
        oscillator: { type: 'sawtooth' },
        envelope: { 
          attack: 0.5, 
          decay: 0.3, 
          sustain: 0.7, 
          release: 2.5 
        },
        volume: volume - 3
      });
      const choirFilter = new Tone.Filter(800, 'lowpass');
      const choirReverb = new Tone.Reverb({ 
        decay: 3.0, 
        wet: 0.6 
      });
      synth.connect(choirFilter);
      choirFilter.connect(choirReverb);
      choirReverb.toDestination();
      effects = [choirFilter, choirReverb];
      break;
      
    case 'harp':
      synth = new Tone.PluckSynth({
        attackNoise: 0.5,
        dampening: 6000,
        resonance: 0.95,
        volume: volume - 1
      });
      const harpReverb = new Tone.Reverb({ 
        decay: 2.0, 
        wet: 0.4 
      });
      synth.connect(harpReverb);
      harpReverb.toDestination();
      effects = [harpReverb];
      break;
      
    case 'saxophone':
      synth = new Tone.Synth({
        oscillator: { type: 'sawtooth' },
        envelope: { 
          attack: 0.05, 
          decay: 0.3, 
          sustain: 0.7, 
          release: 1.2 
        },
        volume: volume
      });
      const saxFilter = new Tone.Filter(1500, 'lowpass');
      const saxVibrato = new Tone.Vibrato(5, 0.05);
      synth.connect(saxFilter);
      saxFilter.connect(saxVibrato);
      saxVibrato.toDestination();
      effects = [saxFilter, saxVibrato];
      break;
      
    default:
      synth = new Tone.Synth({ volume }).toDestination();
  }
  
  // Add dispose method that cleans up effects
  const originalDispose = synth.dispose.bind(synth);
  synth.dispose = () => {
    effects.forEach(effect => {
      if (effect && effect.dispose) effect.dispose();
    });
    originalDispose();
  };
  
  return synth;
};

export const createToneSynth = (synthConfig) => {
  const { typeName, oscillator, volume } = synthConfig;
  const SynthConstructor = synthTypes[typeName] || Tone.Synth;
  let synthOptions = { volume };

  if (SynthConstructor === Tone.Synth && oscillator) {
    synthOptions.oscillator = { type: oscillator };
    synthOptions.envelope = defaultEnvelope;
  } else if (SynthConstructor === Tone.PluckSynth) {
    synthOptions.attackNoise = 0.5;
    synthOptions.dampening = 4000;
    synthOptions.resonance = 0.7;
  } else if (SynthConstructor === Tone.MembraneSynth) {
    synthOptions = { ...membraneEnvelope, volume };
  } else {
    synthOptions.envelope = defaultEnvelope;
    if (SynthConstructor === Tone.AMSynth) synthOptions.harmonicity = 3;
    if (SynthConstructor === Tone.FMSynth) synthOptions.harmonicity = 3;
  }

  // EMERGENCY FIX: Use regular Synth instead of PolySynth to eliminate disposal issues
  return new SynthConstructor(synthOptions).toDestination();
};